import { useState } from "react";
import { Link } from "react-router-dom";
import { money, pct } from "@/lib/api";
import TradeDialog from "@/components/invest/TradeDialog";

const TABS = [["all", "All assets"], ["stock", "Stocks"], ["property", "Real estate"]];

export default function HoldingsTable({ p, onDone }) {
  const [tab, setTab] = useState("all");
  const [selling, setSelling] = useState(null);
  const rows = p.holdings.filter((h) => tab === "all" || h.asset_type === tab);

  return (
    <div className="panel p-5 sm:p-8" data-testid="holdings-table">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="eyebrow">Holdings</p>
        <div className="grid w-full grid-cols-3 rounded-full border border-line p-1 sm:inline-flex sm:w-auto">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`holdings-tab-${k}`}
              className={`rounded-full px-3 py-2.5 text-xs transition-colors sm:px-4 sm:py-1.5 duration-200 ${tab === k ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-6 space-y-3 md:hidden" data-testid="holdings-list-mobile">
        {rows.map((h) => (
          <div key={h.id} className="rounded-xl border border-line bg-obsidian/40 p-4" data-testid={`holding-card-${h.asset_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Link to={`/${h.asset_type === "stock" ? "markets" : "properties"}/${h.asset_id}`} className="font-medium text-white">{h.label}</Link>
                <p className="truncate text-xs text-slate-500">{h.subtitle}{h.rental_yield != null && ` · ${h.rental_yield}% yield`}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-mono text-white">{money(h.market_value)}</p>
                <p className={`font-mono text-xs ${h.gain >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct(h.gain_pct)}</p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-400">
              <span className="font-mono">{h.quantity} × {money(h.price)}</span>
              {h.listed && <button onClick={() => setSelling(h)} className="rounded-full border border-line px-5 py-2.5 text-xs text-slate-300 active:border-amber-600" data-testid={`holding-sell-button-mobile-${h.asset_id}`}>Sell</button>}
            </div>
          </div>
        ))}
        {!rows.length && (
          <p className="py-10 text-center text-sm text-slate-500" data-testid="holdings-empty-mobile">
            No holdings yet. Browse <Link to="/markets" className="text-emerald-400">stocks</Link> or <Link to="/properties" className="text-amber-500">properties</Link>.
          </p>
        )}
      </div>
      <div className="mt-6 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[680px] text-left text-sm">
          <thead>
            <tr className="border-b border-line text-slate-500">
              <th className="py-3 pr-4 font-medium">Asset</th>
              <th className="py-3 pr-4 text-right font-medium">Shares</th>
              <th className="py-3 pr-4 text-right font-medium">Avg cost</th>
              <th className="py-3 pr-4 text-right font-medium">Price</th>
              <th className="py-3 pr-4 text-right font-medium">Value</th>
              <th className="py-3 pr-4 text-right font-medium">Gain</th>
              <th className="py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.id} className="border-b border-line/60 last:border-0" data-testid={`holding-row-${h.asset_id}`}>
                <td className="py-4 pr-4">
                  <Link to={`/${h.asset_type === "stock" ? "markets" : "properties"}/${h.asset_id}`} className="font-medium text-white hover:text-emerald-400">{h.label}</Link>
                  <p className="text-xs text-slate-500">{h.subtitle}{h.rental_yield != null && ` · ${h.rental_yield}% yield`}</p>
                </td>
                <td className="py-4 pr-4 text-right font-mono">{h.quantity}</td>
                <td className="py-4 pr-4 text-right font-mono text-slate-400">{money(h.avg_cost)}</td>
                <td className="py-4 pr-4 text-right font-mono">{money(h.price)}</td>
                <td className="py-4 pr-4 text-right font-mono text-white">{money(h.market_value)}</td>
                <td className={`py-4 pr-4 text-right font-mono ${h.gain >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct(h.gain_pct)}</td>
                <td className="py-4 text-right">
                  {h.listed && <button onClick={() => setSelling(h)} className="rounded-full border border-line px-3 py-1 text-xs text-slate-300 transition-colors hover:border-amber-600 hover:text-amber-500" data-testid={`holding-sell-button-${h.asset_id}`}>Sell</button>}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} className="py-12 text-center text-slate-500" data-testid="holdings-empty">
                No holdings yet. Browse <Link to="/markets" className="text-emerald-400">stocks</Link> or <Link to="/properties" className="text-amber-500">properties</Link>.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {selling && (
        <TradeDialog open={!!selling} onOpenChange={(o) => !o && setSelling(null)} mode="sell" assetType={selling.asset_type}
          assetId={selling.asset_id} label={selling.label} price={selling.price} maxQty={selling.quantity} onDone={onDone} />
      )}
    </div>
  );
}
